/**
 * @fileoverview Factory functions for creating GridColDef configurations for
 * MUI DataGrid columns. Provides standardised field definitions for common data
 * types including strings, numbers, dates, booleans, selects, and JSON fields.
 */

import React from 'react';
import { GridColDef, GridRenderCellParams, GridRenderEditCellParams, GridValidRowModel } from '@mui/x-data-grid';
import { FIELD_TYPE_WIDTHS, FIELD_WIDTHS } from './constants';
import { createOptionLoader } from './option-loader';
import { SelectOption } from './types';

const defaultAccountLoader = createOptionLoader('account', 'username');

// Re-export field width constants for use in other modules
export { FIELD_TYPE_WIDTHS, FIELD_WIDTHS };

/**
 * Converts a date string or Date to a Date object. Returns undefined for falsy values.
 */
function toDate(value: string | Date | null | undefined): Date | undefined {
  if (!value) return undefined;
  return new Date(value);
}

/**
 * Creates an ID field configuration.
 */
export function createIdField(width: number = FIELD_TYPE_WIDTHS.ID): GridColDef {
  return {
    field: 'id',
    headerName: 'ID',
    width,
    editable: false,
  };
}

/**
 * Creates a name field configuration.
 */
export function createNameField(width: number = FIELD_TYPE_WIDTHS.NAME): GridColDef {
  return {
    field: 'name',
    headerName: 'Name',
    width,
  };
}

/**
 * Creates a created_at timestamp field configuration.
 */
export function createCreatedAtField(width: number = FIELD_TYPE_WIDTHS.DATE): GridColDef {
  return {
    field: 'created_at',
    headerName: 'Created',
    type: 'dateTime',
    valueGetter: (value: string | null | undefined): Date | undefined => toDate(value),
    width,
    editable: false,
  };
}

/**
 * Creates an updated_at timestamp field configuration.
 */
export function createUpdatedAtField(width: number = FIELD_TYPE_WIDTHS.DATE): GridColDef {
  return {
    field: 'updated_at',
    headerName: 'Updated',
    type: 'dateTime',
    valueGetter: (value: string | null | undefined): Date | undefined => toDate(value),
    width,
    editable: false,
  };
}

/**
 * Creates a date field configuration.
 */
export function createDateField(
  field: string,
  headerName: string,
  width: number = FIELD_TYPE_WIDTHS.DATE,
  editable: boolean = true
): GridColDef {
  return {
    field,
    headerName,
    type: 'date',
    valueGetter: (value: string | null | undefined): Date | undefined => toDate(value),
    width,
    editable,
  };
}

/**
 * Creates a dateTime field configuration.
 */
export function createDateTimeField(
  field: string,
  headerName: string,
  width: number = FIELD_TYPE_WIDTHS.DATE,
  editable: boolean = true
): GridColDef {
  return {
    field,
    headerName,
    type: 'dateTime',
    valueGetter: (value: string | null | undefined): Date | undefined => toDate(value),
    width,
    editable,
  };
}

/**
 * Creates a string field configuration.
 */
export function createStringField(
  field: string,
  headerName: string,
  width: number = FIELD_WIDTHS.MEDIUM
): GridColDef {
  return {
    field,
    headerName,
    width,
    type: 'string',
  };
}

/**
 * Creates a JSON field configuration with formatted display and edit capabilities.
 */
export function createJsonField(
  field: string,
  headerName: string,
  width: number = FIELD_WIDTHS.XLARGE
): GridColDef {
  return {
    field,
    headerName,
    width,
    type: 'string',
    renderCell: (params: GridRenderCellParams<GridValidRowModel, string | null>): React.ReactNode => {
      if (!params.value) return '';
      
      try {
        const jsonValue: unknown = JSON.parse(params.value);
        return React.createElement('div', {
          style: { 
            fontFamily: 'monospace', 
            fontSize: '0.85em',
            maxWidth: '100%',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
          }
        }, JSON.stringify(jsonValue));
      } catch {
        return String(params.value);
      }
    },
    renderEditCell: (params: GridRenderEditCellParams): React.ReactNode => {
      const handleChange = (event: React.ChangeEvent<HTMLTextAreaElement>): void => {
        void params.api.setEditCellValue({ 
          id: params.id, 
          field: params.field, 
          value: event.target.value 
        });
      };

      let displayValue = '';
      try {
        const jsonValue: unknown = JSON.parse(String(params.value || ''));
        displayValue = JSON.stringify(jsonValue, null, 2);
      } catch {
        displayValue = String(params.value || '');
      }

      return React.createElement('textarea', {
        value: displayValue,
        onChange: handleChange,
        style: {
          width: '100%',
          height: '80px',
          fontFamily: 'monospace',
          fontSize: '0.85em',
          border: '1px solid #ccc',
          borderRadius: '4px',
          padding: '4px'
        },
        placeholder: 'Enter valid JSON...'
      });
    },
  };
}

/**
 * Creates a number field configuration.
 */
export function createNumberField(
  field: string,
  headerName: string,
  width: number = FIELD_WIDTHS.SMALL
): GridColDef {
  return {
    field,
    headerName,
    width,
    type: 'number',
  };
}

/**
 * Creates a single-select field configuration with dropdown options.
 */
export function createSelectField(
  field: string,
  headerName: string,
  options: SelectOption[],
  width: number = FIELD_WIDTHS.MEDIUM
): GridColDef {
  return {
    field,
    headerName,
    width,
    type: 'singleSelect',
    valueOptions: options,
    renderCell: (params: GridRenderCellParams<GridValidRowModel, string | null>): string => {
      const option = options.find(opt => opt.value === params.value);
      return option ? option.label : String(params.value || '');
    },
    valueSetter: (value: string | null): string | null => value,
  };
}

/**
 * Creates a boolean field configuration.
 */
export function createBooleanField(
  field: string,
  headerName: string,
  width: number = FIELD_WIDTHS.SMALL
): GridColDef {
  return {
    field,
    headerName,
    width,
    type: 'boolean',
  };
}

/**
 * Creates a deleted_at timestamp field configuration for soft delete.
 */
export function createDeletedAtField(width: number = FIELD_TYPE_WIDTHS.DATE): GridColDef {
  return {
    field: 'deleted_at',
    headerName: 'Deleted At',
    type: 'dateTime',
    valueGetter: (value: string | null | undefined): Date | undefined => toDate(value),
    width,
    editable: false,
  };
}

/**
 * Creates an archived_at timestamp field configuration for soft archive.
 */
export function createArchivedAtField(width: number = FIELD_TYPE_WIDTHS.DATE): GridColDef {
  return {
    field: 'archived_at',
    headerName: 'Archived At',
    type: 'dateTime',
    valueGetter: (value: string | null | undefined): Date | undefined => toDate(value),
    width,
    editable: false,
  };
}

/**
 * Creates a display_order field configuration.
 */
export function createDisplayOrderField(width: number = FIELD_WIDTHS.SMALL): GridColDef {
  return {
    field: 'display_order',
    headerName: 'Order',
    width,
    type: 'number',
  };
}

export interface CreateAccountTrackingFieldsOptions {
  /** Dropdown options for account fields. Defaults to account usernames from the API. */
  accountOptions?: SelectOption[];
  /** When true, includes archived_by_account_id. Default false. */
  includeArchived?: boolean;
  /** When false, omits created_by_account_id (e.g. the account entity). Default true. */
  includeCreated?: boolean;
  width?: number;
}

/**
 * Creates read-only select columns for account audit fields (created/updated/archived/deleted by).
 * Common on entities with trackAccountId enabled on the backend.
 */
export function createAccountTrackingFields(
  options: CreateAccountTrackingFieldsOptions = {}
): GridColDef[] {
  const {
    accountOptions = defaultAccountLoader.getOptions(),
    includeArchived = false,
    includeCreated = true,
    width = FIELD_WIDTHS.MEDIUM,
  } = options;

  const fields: GridColDef[] = [];

  if (includeCreated) {
    fields.push({
      ...createSelectField('created_by_account_id', 'Created By', accountOptions, width),
      editable: false,
    });
  }

  fields.push({
    ...createSelectField('updated_by_account_id', 'Updated By', accountOptions, width),
    editable: false,
  });

  if (includeArchived) {
    fields.push({
      ...createSelectField('archived_by_account_id', 'Archived By', accountOptions, width),
      editable: false,
    });
  }

  fields.push({
    ...createSelectField('deleted_by_account_id', 'Deleted By', accountOptions, width),
    editable: false,
  });

  return fields;
}

/**
 * Creates standard audit timestamp fields (deleted_at, created_at, updated_at).
 * Common across most entity data sources.
 */
export function createAuditFields(width: number = FIELD_TYPE_WIDTHS.DATE): GridColDef[] {
  return [
    createDeletedAtField(width),
    createCreatedAtField(width),
    createUpdatedAtField(width),
  ];
}

/**
 * Creates audit fields with archived_at for entities supporting soft archive.
 */
export function createAuditFieldsWithArchive(width: number = FIELD_TYPE_WIDTHS.DATE): GridColDef[] {
  return [
    createArchivedAtField(width),
    ...createAuditFields(width),
  ];
}

/**
 * Creates common fields for lookup/reference entities:
 * id, name, description, display_order, metadata.
 */
export function createLookupEntityFields(idWidth: number = FIELD_WIDTHS.SMALL): GridColDef[] {
  return [
    createIdField(idWidth),
    createNameField(FIELD_TYPE_WIDTHS.NAME),
    createStringField('description', 'Description', FIELD_WIDTHS.XLARGE),
    createDisplayOrderField(FIELD_WIDTHS.SMALL),
    createJsonField('metadata', 'Metadata', FIELD_WIDTHS.XLARGE),
  ];
}
